// src/utils/ensureAuthSession.js

/**
 * Stellt sicher, dass am AUTH-Server (/auth) eine gültige Session existiert.
 * Falls nur die Mentoring-Session vorhanden ist, „heften“ wir sie via
 * POST /auth/sso-attach an den Auth-Server an.
 *
 * WICHTIG:
 * - Verwendet ausschließlich fetch (keine axios-Instanzen), um Zirkularimporte zu vermeiden.
 * - Arbeitet robust gegen unterschiedliche whoami-Shapes.
 */

/* ============================== Helpers ============================== */

function safeJson(res) {
  if (!res || typeof res.json !== 'function') return Promise.resolve({});
  return res.json().catch(() => ({}));
}

async function getAuthWhoami() {
  try {
    const r = await fetch('/auth/whoami', { credentials: 'include' });
    if (!r.ok) return { isAuthenticated: false, user: null };
    const j = await safeJson(r);
    return {
      isAuthenticated: !!j?.isAuthenticated || !!(j?.user && j.user.email),
      user: j?.user || null,
      raw: j,
    };
  } catch {
    return { isAuthenticated: false, user: null };
  }
}

async function getMentoringWhoami() {
  try {
    const r = await fetch('/mentoring/whoami', { credentials: 'include' });
    if (!r.ok) return { user: null, raw: null };

    const j = await safeJson(r);
    // verschiedene mögliche Shapes abdecken
    const u = j?.user || j?.data?.user || j?.profile || j?.hr_profile || null;

    return { user: u, raw: j };
  } catch {
    return { user: null, raw: null };
  }
}

async function getCsrfFromAuth() {
  try {
    const r = await fetch('/auth/csrf-token', { credentials: 'include' });
    const j = await safeJson(r);
    return j?.csrfToken || '';
  } catch {
    return '';
  }
}

/**
 * Baut den Payload für /auth/sso-attach aus einer generischen Mentoring-User-Shape.
 */
function buildAttachPayloadFromMentoringUser(mUser) {
  if (!mUser) return null;

  return {
    user: {
      // Minimal ausreichend
      email: mUser.email || null,

      // Hilfreiche Zusatzinfos (werden serverseitig einfach übernommen)
      role: mUser.role || mUser.user_role || 'hr_user',

      // customer/tenant
      customer_id:
        mUser.customer_id ??
        mUser.customerId ??
        mUser.tenant_id ??
        mUser.tenantId ??
        mUser.client_id ??
        mUser.clientId ??
        null,

      // Namen (optional)
      firstName: mUser.firstname ?? mUser.first_name ?? mUser.firstName ?? null,
      lastName: mUser.lastname ?? mUser.last_name ?? mUser.lastName ?? null,

      // IDs (optional)
      id: mUser.id ?? mUser.user_id ?? null,
      user_id: mUser.user_id ?? mUser.id ?? null,
    },
  };
}

/* ============================== Main ============================== */

/**
 * Stellt sicher, dass die AUTH-Session existiert.
 * @returns {Promise<object|null>} whoami-Response (z.B. {ok:true,user:{...}}) oder null
 */
export default async function ensureAuthSession() {
  try {
    // 0) Schnelltest: existiert bereits eine AUTH-Session?
    const a0 = await getAuthWhoami();
    if (a0.isAuthenticated && a0.user?.email) {
      return a0.raw || { ok: true, user: a0.user };
    }

    // 1) CSRF (nicht zwingend nötig; schadet aber nicht, falls Middleware aktiv ist)
    const csrf = await getCsrfFromAuth();

    // 2) Mentoring-Identität holen
    let { user: mUser } = await getMentoringWhoami();

    // 2b) Wenn keine Mentoring-Identität vorhanden → zweiter Versuch (leicht verzögert)
    if (!mUser || !mUser.email) {
      try {
        await new Promise((r) => setTimeout(r, 50));
        const again = await getMentoringWhoami();
        mUser = again.user;
      } catch {
        /* ignore */
      }
    }

    // Wenn keine Mentoring-Identität vorliegt, können wir AUTH nicht befüllen
    if (!mUser || !mUser.email) {
      return null;
    }

    // 3) AUTH-Session via /auth/sso-attach „anheften“
    const payload = buildAttachPayloadFromMentoringUser(mUser);
    if (payload?.user?.email) {
      await fetch('/auth/sso-attach', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      }).catch(() => {
        /* ignore network error here; we'll verify with whoami next */
      });
    }

    // 4) final prüfen
    const a1 = await getAuthWhoami();
    if (a1.isAuthenticated && a1.user?.email) {
      return a1.raw || { ok: true, user: a1.user };
    }

    return null;
  } catch {
    return null;
  }
}

/* Optional für Debug in der Browser-Konsole */
export async function ensureAuthSessionDebug() {
  const res = await ensureAuthSession();
  // eslint-disable-next-line no-console
  console.log('[ensureAuthSession] result:', res);
  return res;
}

if (typeof window !== 'undefined') {
  // @ts-ignore
  window.ensureAuthSession = ensureAuthSession;
}

